import React, { useEffect, useState } from 'react';
import { monitorAPI } from '../services/api';
import toast from 'react-hot-toast';
import { useAuthStore } from '../stores/authStore';
import MetricCard from '../components/Common/MetricCard';

type MonitorTab = 'operations' | 'logs' | 'alerts';

const StarlinkMonitor: React.FC = () => {
  const [running, setRunning] = useState<boolean | null>(null);
  const [logs, setLogs] = useState<string[]>([]);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [email, setEmail] = useState<string>('');
  const [savingEmail, setSavingEmail] = useState(false);
  const [activeTab, setActiveTab] = useState<MonitorTab>('operations');
  const [recipients, setRecipients] = useState<Array<{
    userId: number; username: string; email: string; role: string;
    hasPhoneNumber: boolean; notifyEmail: boolean; notifyWhatsapp: boolean;
  }>>([]);
  const [loadingRecipients, setLoadingRecipients] = useState(false);
  const [savingRecipientId, setSavingRecipientId] = useState<number | null>(null);
  const [auditLog, setAuditLog] = useState<Array<{
    id: number; action: 'start' | 'stop'; source: 'manual' | 'auto';
    userId: number | null; email: string | null; ipAddress: string | null;
    metadata: Record<string, any> | null; createdAt: string;
  }>>([]);
  const [loadingAuditLog, setLoadingAuditLog] = useState(false);
  const { user } = useAuthStore();

  const fetchStatus = async () => {
    try {
      setLoadingStatus(true);
      const data = await monitorAPI.getStatus();
      setRunning(Boolean(data.running));
    } catch (error) {
      console.error('Failed to fetch monitor status', error);
      toast.error('Failed to fetch monitor status');
    } finally {
      setLoadingStatus(false);
    }
  };

  const fetchLogs = async (maxLines: number = 200) => {
    try {
      setLoadingLogs(true);
      const data = await monitorAPI.getLogs(maxLines);
      setLogs(data as string[]);
    } catch (error) {
      console.error('Failed to fetch monitor logs', error);
      toast.error('Failed to fetch monitor logs');
    } finally {
      setLoadingLogs(false);
    }
  };

  const fetchEmail = async () => {
    try {
      const e = await monitorAPI.getNotificationEmail();
      setEmail(e ?? '');
    } catch (error) {
      console.error('Failed to fetch notification email', error);
    }
  };

  const handleStart = async () => {
    try {
      setRunning(true);
      await monitorAPI.startMonitor();
      toast.success('Monitor started successfully 🚀');
      fetchStatus();
    } catch (error: any) {
      setRunning(false);
      const msg = error.response?.data?.message || 'Failed to start monitor';
      toast.error(msg);
    }
  };

  const handleStop = async () => {
    try {
      setRunning(false);
      await monitorAPI.stopMonitor();
      toast.success('Monitor stopped successfully ✋');
      fetchStatus();
    } catch (error: any) {
      fetchStatus();
      const msg = error.response?.data?.message || 'Failed to stop monitor';
      toast.error(msg);
    }
  };

  const handleDownloadLogs = async () => {
    try {
      const blob = await monitorAPI.downloadLogs(500);
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `starlink_monitor_logs_${Date.now()}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      toast.success('Logs downloaded successfully');
    } catch (error) {
      console.error('Failed to download logs', error);
      toast.error('Failed to download logs');
    }
  };

  const fetchAlertRecipients = async () => {
    try {
      setLoadingRecipients(true);
      const data = await monitorAPI.getAlertRecipients();
      setRecipients(data);
    } catch (error) {
      console.error('Failed to fetch alert recipients', error);
    } finally {
      setLoadingRecipients(false);
    }
  };

  const fetchAuditLog = async () => {
    try {
      setLoadingAuditLog(true);
      const data = await monitorAPI.getAuditLog();
      setAuditLog(data);
    } catch (error) {
      console.error('Failed to fetch monitor audit log', error);
    } finally {
      setLoadingAuditLog(false);
    }
  };

  const handleToggleRecipient = async (userId: number, field: 'notifyEmail' | 'notifyWhatsapp', value: boolean) => {
    setRecipients(prev => prev.map(r => r.userId === userId ? { ...r, [field]: value } : r));
    try {
      setSavingRecipientId(userId);
      await monitorAPI.updateAlertRecipient(userId, { [field]: value });
    } catch (error) {
      toast.error('Failed to update alert recipient');
      fetchAlertRecipients();
    } finally {
      setSavingRecipientId(null);
    }
  };

  const handleSaveEmail = async () => {
    try {
      setSavingEmail(true);
      await monitorAPI.setNotificationEmail(email.trim() || null);
      toast.success('Notification email saved');
    } catch (error) {
      toast.error('Failed to save email');
    } finally {
      setSavingEmail(false);
    }
  };

  const handleTrigger = async () => {
    try {
      const response = await monitorAPI.triggerMonitor();
      toast.success('Monitor cycle completed successfully 🚀');
      
      // Show individual messages as toasts if provided
      if (response.messages && Array.isArray(response.messages) && response.messages.length > 0) {
        response.messages.forEach((message: string) => {
          toast.success(message);
        });
      }
      
      // Refresh logs after trigger
      fetchLogs();
    } catch (error: any) {
      const msg = error.response?.data?.message || 'Failed to trigger monitor';
      toast.error(msg);
    }
  };

  useEffect(() => {
    fetchStatus();
    fetchLogs();
    fetchEmail();
    const interval = setInterval(() => fetchStatus(), 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (activeTab === 'alerts') {
      fetchAlertRecipients();
      fetchAuditLog();
    }
  }, [activeTab]);

  useEffect(() => {
    const ev = new EventSource('/api/monitor/logs/stream');
    ev.onmessage = (e) => {
      setLogs(prev => [...prev.slice(-399), e.data]);
    };
    return () => ev.close();
  }, []);

  const latestLog = logs.length > 0 ? logs[logs.length - 1] : null;
  const errorCount = logs.filter((entry) => entry.toLowerCase().includes('error')).length;
  const warnCount = logs.filter((entry) => entry.toLowerCase().includes('warn')).length;
  const monitorTabs: { id: MonitorTab; label: string; hint: string }[] = [
    { id: 'operations', label: 'Operations', hint: 'Status, controls, live summary' },
    { id: 'logs', label: 'Logs', hint: 'Real-time monitor output' },
    { id: 'alerts', label: 'Alerts', hint: 'Notification email and delivery context' },
  ];

  return (
    <div className="space-y-8">
      {/* Hero Header */}
      <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-r from-slate-900 via-blue-900 to-indigo-900 p-8 text-white shadow-2xl">
        <div className="absolute inset-0 opacity-30 [background:radial-gradient(circle_at_15%_20%,rgba(56,189,248,0.28),transparent_45%),radial-gradient(circle_at_85%_25%,rgba(167,139,250,0.22),transparent_55%)]" />
        <div className="flex flex-col md:flex-row md:items-center md:justify-between">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-semibold text-white/80 backdrop-blur">
              <span className={`h-2 w-2 rounded-full ${loadingStatus ? 'bg-amber-400' : running ? 'bg-emerald-400' : 'bg-rose-400'}`} />
              {loadingStatus ? 'Checking…' : (running ? 'Monitor running' : 'Monitor stopped')}
              </div>
            <h1 className="mt-3 text-3xl font-black tracking-tight sm:text-4xl">Starlink Monitor</h1>
            <p className="mt-2 text-white/70 max-w-2xl">Control the monitor, review logs, and configure notification email.</p>
            <div className="mt-4 flex flex-wrap items-center gap-3 text-sm text-white/70">
              <span className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-3 py-2">
                <i className="fas fa-clock text-white/60"></i>
                Updated: {new Date().toLocaleTimeString()}
              </span>
            </div>
          </div>
          <div className="mt-6 md:mt-0 flex space-x-3">
            {running ? (
              <button
                onClick={user?.isSuperAdmin ? handleStop : undefined}
                disabled={loadingStatus || !user?.isSuperAdmin}
                className={`px-6 py-3 rounded-2xl font-semibold transition-all duration-200 shadow-lg ${
                  user?.isSuperAdmin 
                    ? 'bg-red-500 hover:bg-red-600 text-white' 
                    : 'bg-red-500 text-white opacity-60 cursor-not-allowed'
                } disabled:opacity-50`}
                title={!user?.isSuperAdmin ? 'Only Super Admin can control the monitor' : ''}
              >
                <i className="fas fa-stop mr-2"></i> Stop Monitor
              </button>
            ) : (
              <button
                onClick={user?.isSuperAdmin ? handleStart : undefined}
                disabled={loadingStatus || !user?.isSuperAdmin}
                className={`px-6 py-3 rounded-2xl font-semibold transition-all duration-200 shadow-lg ${
                  user?.isSuperAdmin 
                    ? 'bg-white text-slate-900 hover:bg-slate-100' 
                    : 'bg-white text-slate-900 opacity-60 cursor-not-allowed'
                } disabled:opacity-50`}
                title={!user?.isSuperAdmin ? 'Only Super Admin can control the monitor' : ''}
              >
                <i className="fas fa-play mr-2"></i> Start Monitor
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-4">
        <MetricCard
          label="Monitor state"
          value={loadingStatus ? 'Checking…' : running ? 'Running' : 'Stopped'}
          hint="Live Starlink monitor health"
          icon="fas fa-satellite-dish"
          accent={running ? 'green' : 'red'}
        />
        <MetricCard
          label="Log entries"
          value={logs.length}
          hint="Buffered on this page"
          icon="fas fa-stream"
          accent="blue"
        />
        <MetricCard
          label="Warnings"
          value={warnCount}
          hint="Lines containing warn"
          icon="fas fa-triangle-exclamation"
          accent="amber"
        />
        <MetricCard
          label="Errors"
          value={errorCount}
          hint="Lines containing error"
          icon="fas fa-circle-exclamation"
          accent="red"
        />
      </div>

      <div className="rounded-3xl border border-gray-100 bg-white p-3 shadow-sm">
        <div className="flex flex-wrap gap-3">
          {monitorTabs.map((tab) => {
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`min-w-[12rem] flex-1 rounded-2xl border px-4 py-3 text-left transition-all ${
                  isActive
                    ? 'border-slate-900 bg-gradient-to-r from-slate-900 via-blue-900 to-indigo-900 text-white shadow-lg'
                    : 'border-gray-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50'
                }`}
              >
                <div className="text-sm font-bold">{tab.label}</div>
                <p className={`mt-1 text-xs ${isActive ? 'text-white/75' : 'text-slate-500'}`}>{tab.hint}</p>
              </button>
            );
          })}
        </div>
      </div>

      {activeTab === 'operations' && (
        <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1.2fr)_minmax(0,0.8fr)]">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <h2 className="text-xl font-black text-gray-900 mb-6">Monitor controls</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <button
                onClick={() => fetchLogs()}
                disabled={loadingLogs}
                className="flex items-center justify-center px-6 py-4 border border-blue-200 text-blue-700 hover:bg-blue-50 rounded-2xl transition-all duration-200 disabled:opacity-50"
              >
                <i className={`fas fa-sync-alt mr-3 ${loadingLogs ? 'animate-spin' : ''}`}></i>
                <div className="text-left">
                  <div className="font-medium">Refresh Logs</div>
                  <div className="text-sm text-gray-600">Update log display</div>
                </div>
              </button>

              <button
                onClick={handleTrigger}
                disabled={!running}
                className={`flex items-center justify-center px-6 py-4 border rounded-2xl transition-all duration-200 ${
                  running
                    ? 'border-green-200 text-green-700 hover:bg-green-50'
                    : 'border-gray-200 text-gray-400 cursor-not-allowed'
                }`}
                title={!running ? 'Monitor must be running to trigger' : 'Manually trigger a monitor cycle'}
              >
                <i className="fas fa-play-circle mr-3"></i>
                <div className="text-left">
                  <div className="font-medium">Trigger Cycle</div>
                  <div className="text-sm text-gray-600">Run monitor now</div>
                </div>
              </button>

              <button
                onClick={handleDownloadLogs}
                className="flex items-center justify-center px-6 py-4 border border-purple-200 text-purple-700 hover:bg-purple-50 rounded-2xl transition-all duration-200"
              >
                <i className="fas fa-file-download mr-3"></i>
                <div className="text-left">
                  <div className="font-medium">Download PDF</div>
                  <div className="text-sm text-gray-600">Export logs as PDF</div>
                </div>
              </button>

              <div className={`flex items-center justify-center px-6 py-4 border rounded-2xl ${
                loadingStatus ? 'border-yellow-200' : running ? 'border-green-200' : 'border-red-200'
              }`}>
                <i className={`fas fa-satellite mr-3 text-2xl ${
                  loadingStatus ? 'text-yellow-600' : running ? 'text-green-600' : 'text-red-600'
                }`}></i>
                <div className="text-left">
                  <div className={`font-medium ${
                    loadingStatus ? 'text-yellow-700' : running ? 'text-green-700' : 'text-red-700'
                  }`}>
                    {loadingStatus ? 'Checking...' : running ? 'RUNNING' : 'STOPPED'}
                  </div>
                  <div className="text-sm text-gray-600">Monitor Status</div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <h2 className="text-xl font-black text-gray-900">Latest activity</h2>
            <div className="mt-5 rounded-2xl bg-slate-950 p-5 text-white shadow-inner">
              {latestLog ? (
                <>
                  <div className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-emerald-300">
                    <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse"></span>
                    Most recent log line
                  </div>
                  <pre className="whitespace-pre-wrap text-sm leading-6 text-white/90 font-mono">{latestLog}</pre>
                </>
              ) : (
                <p className="text-sm text-white/70">No live activity yet.</p>
              )}
            </div>

            <div className="mt-5 grid grid-cols-1 gap-3">
              <div className="rounded-2xl bg-slate-50 p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Realtime feed</p>
                <p className="mt-1 text-sm text-slate-700">This page listens to the monitor log stream and appends new entries as they arrive.</p>
              </div>
              <div className="rounded-2xl bg-slate-50 p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Operator note</p>
                <p className="mt-1 text-sm text-slate-700">Keep the logs tab open during incident response to watch the feed update live.</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'logs' && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <h3 className="text-xl font-black text-gray-900">Live logs</h3>
                <div className="ml-3 flex items-center">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  <span className="ml-2 text-sm text-gray-600">Real-time</span>
                </div>
              </div>
              <div className="text-sm text-gray-500">
                {logs.length} log entries
              </div>
            </div>
          </div>

          <div className="p-6">
            {loadingLogs ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600 mr-3"></div>
                <span className="text-gray-500">Loading logs...</span>
              </div>
            ) : logs.length === 0 ? (
              <div className="text-center py-12">
                <i className="fas fa-file-alt text-4xl text-gray-300 mb-4"></i>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No logs available</h3>
                <p className="text-gray-500">Start the monitor to begin logging activity</p>
              </div>
            ) : (
              <div className="rounded-3xl bg-slate-950 p-4 shadow-inner">
                <div className="mb-3 flex items-center justify-between text-xs font-semibold uppercase tracking-wide text-white/50">
                  <span>Starlink monitor stream</span>
                  <span>Newest entries append automatically</span>
                </div>
                <div className="max-h-[34rem] overflow-y-auto rounded-2xl bg-black/20 p-4">
                  <pre className="whitespace-pre-wrap text-sm leading-6 text-white font-mono">
                    {logs.join('\n')}
                  </pre>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'alerts' && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center mb-6">
            <div className="p-3 bg-blue-100 rounded-xl mr-4">
              <i className="fas fa-envelope text-blue-600 text-xl"></i>
            </div>
            <div>
              <h3 className="text-xl font-black text-gray-900">Email notifications</h3>
              <p className="text-gray-600">Configure email alerts for monitor errors</p>
            </div>
          </div>

          <div className="bg-gray-50 p-4 rounded-2xl">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notification Email
                </label>
                <input
                  type="email"
                  placeholder="admin@yourcompany.com"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  className="w-full rounded-2xl bg-white px-4 py-3 ring-1 ring-inset ring-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500/30 transition-all"
                />
              </div>
              <div className="flex items-end">
                <button
                  onClick={handleSaveEmail}
                  disabled={savingEmail}
                  className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:brightness-110 text-white px-6 py-3 rounded-2xl disabled:opacity-50 transition-all duration-200 font-semibold shadow-lg"
                >
                  {savingEmail ? (
                    <><i className="fas fa-spinner animate-spin mr-2"></i>Saving...</>
                  ) : (
                    <><i className="fas fa-save mr-2"></i>Save</>
                  )}
                </button>
              </div>
            </div>
            <p className="text-xs text-gray-600 mt-3 flex items-center">
              <i className="fas fa-info-circle mr-2"></i>
              Emails are sent immediately when errors are logged to the monitor
            </p>
          </div>

          <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="rounded-2xl bg-slate-50 p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Delivery mode</p>
              <p className="mt-2 text-sm text-slate-700">Immediate email alerts tied to the monitor error flow.</p>
            </div>
            <div className="rounded-2xl bg-slate-50 p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Current configured address</p>
              <p className="mt-2 text-sm font-semibold text-slate-900">{email || 'No notification email configured'}</p>
            </div>
          </div>

          {/* Per-user alert recipients */}
          <div className="mt-8 border-t border-gray-100 pt-6">
            <div className="flex items-center mb-4">
              <div className="p-3 bg-emerald-100 rounded-xl mr-4">
                <i className="fab fa-whatsapp text-emerald-600 text-xl"></i>
              </div>
              <div>
                <h3 className="text-xl font-black text-gray-900">Who gets notified on start/stop</h3>
                <p className="text-gray-600">Pick email and/or WhatsApp per Admin/Super Admin. Covers manual starts and stops from this dashboard, and automatic stops after an error.</p>
              </div>
            </div>

            {!user?.isSuperAdmin && (
              <p className="mb-4 text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-xl px-4 py-2">
                Only Super Admins can change these settings.
              </p>
            )}

            {loadingRecipients ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-emerald-600 mr-3"></div>
                <span className="text-gray-500 text-sm">Loading recipients...</span>
              </div>
            ) : recipients.length === 0 ? (
              <p className="text-sm text-gray-500">No Admin or Super Admin users found.</p>
            ) : (
              <div className="overflow-x-auto rounded-2xl border border-gray-100">
                <table className="min-w-full divide-y divide-gray-100 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold text-gray-600">User</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-600">Role</th>
                      <th className="px-4 py-3 text-center font-semibold text-gray-600">Email</th>
                      <th className="px-4 py-3 text-center font-semibold text-gray-600">WhatsApp</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {recipients.map((r) => (
                      <tr key={r.userId}>
                        <td className="px-4 py-3">
                          <div className="font-medium text-gray-900">{r.username}</div>
                          <div className="text-xs text-gray-500">{r.email}</div>
                        </td>
                        <td className="px-4 py-3 text-gray-600 capitalize">{r.role.replace('_', ' ')}</td>
                        <td className="px-4 py-3 text-center">
                          <input
                            type="checkbox"
                            checked={r.notifyEmail}
                            disabled={!user?.isSuperAdmin || savingRecipientId === r.userId}
                            onChange={(e) => handleToggleRecipient(r.userId, 'notifyEmail', e.target.checked)}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                        </td>
                        <td className="px-4 py-3 text-center">
                          <input
                            type="checkbox"
                            checked={r.notifyWhatsapp}
                            disabled={!user?.isSuperAdmin || savingRecipientId === r.userId || !r.hasPhoneNumber}
                            title={!r.hasPhoneNumber ? 'No phone number on file for this user' : ''}
                            onChange={(e) => handleToggleRecipient(r.userId, 'notifyWhatsapp', e.target.checked)}
                            className="h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded disabled:opacity-40"
                          />
                          {!r.hasPhoneNumber && (
                            <div className="mt-1 text-[10px] text-gray-400">No phone on file</div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Start/stop audit trail */}
          <div className="mt-8 border-t border-gray-100 pt-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <div className="p-3 bg-slate-100 rounded-xl mr-4">
                  <i className="fas fa-user-clock text-slate-600 text-xl"></i>
                </div>
                <div>
                  <h3 className="text-xl font-black text-gray-900">Start / stop audit trail</h3>
                  <p className="text-gray-600">Who started or stopped the monitor from this dashboard, and when it stopped or restarted itself. After an automatic stop, it retries on its own 5 minutes later.</p>
                </div>
              </div>
              <button
                onClick={fetchAuditLog}
                disabled={loadingAuditLog}
                className="px-4 py-2 text-sm rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50"
              >
                <i className={`fas fa-sync-alt mr-2 ${loadingAuditLog ? 'animate-spin' : ''}`}></i>Refresh
              </button>
            </div>

            {loadingAuditLog ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-slate-600 mr-3"></div>
                <span className="text-gray-500 text-sm">Loading audit trail...</span>
              </div>
            ) : auditLog.length === 0 ? (
              <p className="text-sm text-gray-500">No start/stop events recorded yet.</p>
            ) : (
              <div className="max-h-96 overflow-y-auto rounded-2xl border border-gray-100 divide-y divide-gray-100">
                {auditLog.map((entry) => (
                  <div key={entry.id} className="flex items-center justify-between px-4 py-3 text-sm">
                    <div className="flex items-center gap-3">
                      <span className={`inline-flex h-2.5 w-2.5 rounded-full ${entry.action === 'start' ? 'bg-emerald-500' : 'bg-red-500'}`} />
                      <div>
                        <div className="font-medium text-gray-900">
                          {entry.action === 'start' ? 'Started' : 'Stopped'}
                          {entry.source === 'auto'
                            ? (entry.action === 'start' ? ' automatically (retry after failure)' : ' automatically (error)')
                            : ` by ${entry.email || 'unknown user'}`}
                        </div>
                        {entry.source === 'auto' && entry.metadata?.reason && (
                          <div className="text-xs text-gray-500">{entry.metadata.reason}</div>
                        )}
                        {entry.source === 'manual' && entry.ipAddress && (
                          <div className="text-xs text-gray-400">from {entry.ipAddress}</div>
                        )}
                      </div>
                    </div>
                    <div className="text-xs text-gray-500 whitespace-nowrap">{new Date(entry.createdAt).toLocaleString()}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default StarlinkMonitor; 
